import { Resources } from 'constants/resources';

import { Fragment } from 'react';

import { usePermissions } from 'hooks/usePermissions';
import { useRecordModal } from 'hooks/useRecordModal';
import { CompaniesArchiveDialog } from 'pages/Companies/components/CompaniesArchiveDialog/CompaniesArchiveDialog';
import { CompaniesDeleteDialog } from 'pages/Companies/components/CompaniesDeleteDialog/CompaniesDeleteDialog';
import { CompaniesUnarchiveDialog } from 'pages/Companies/components/CompaniesUnarchiveDialog/CompaniesUnarchiveDialog';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { CreateButton, LinkButton } from 'shared/mui/Buttons';
import { ArchiveConditionListChip } from 'shared/mui/Chips';
import {
  ContextMenu,
  ContextMenuArchiveButton,
  ContextMenuDeleteButton,
  ContextMenuEntityUserActionsButton,
  ContextMenuShowButton,
} from 'shared/mui/ContextMenu';
import { ContextMenuEditButton } from 'shared/mui/ContextMenu/ContextMenuEditButton/ContextMenuEditButton';
import { ContextMenuUnarchiveButton } from 'shared/mui/ContextMenu/ContextMenuUnarchiveButton/ContextMenuUnarchiveButton';
import { ExportButton } from 'shared/react-admin/Buttons';
import { DateTimeField, FunctionField, TextField, WrapperField } from 'shared/react-admin/Fields';
import { TooltipTextLinkField } from 'shared/react-admin/Fields/TooltipTextLinkField/TooltipTextLinkField';
import { ListPage } from 'shared/react-admin/Pages';
import { Company } from 'types/embedded/company';
import { Permission } from 'types/embedded/permission';

import { StyledDatagrid } from './CompaniesList.styled';
import { CompaniesListFilter } from './CompaniesListFilter';

export const CompaniesList: React.FC = () => {
  const translate = useTypedTranslate();
  const { hasResourcePermissions, hasResourceAccess } = usePermissions();
  const {
    record: recordDelete,
    handleOpenRecordModal: handleOpenRecordModalDelete,
    openRecordModal: openRecordModalDelete,
    handleCloseRecordModal: handleCloseRecordModalDelete,
  } = useRecordModal<Company>();
  const {
    record: recordUnarchive,
    handleOpenRecordModal: handleOpenRecordModalUnarchive,
    openRecordModal: openRecordModalUnarchive,
    handleCloseRecordModal: handleCloseRecordModalUnarchive,
  } = useRecordModal<Company>();
  const {
    record: recordArchive,
    handleOpenRecordModal: handleOpenRecordModalArchive,
    openRecordModal: openRecordModalArchive,
    handleCloseRecordModal: handleCloseRecordModalArchive,
  } = useRecordModal<Company>();

  return (
    <ListPage
      actions={[
        hasResourcePermissions(Resources.Companies, [Permission.Full, Permission.Create]) && (
          <CreateButton key="create_companies" />
        ),
        <ExportButton key="export_companies" />,
      ]}
      empty={false}
      filterDefaultValues={{ archive: false }}
      headerTitle={{
        titleText: translate('app.companies.titleText'),
        tooltipText: translate('app.companies.tooltipText'),
      }}
      sort={{ field: 'id', order: 'DESC' }}
    >
      <CompaniesListFilter />
      <StyledDatagrid bulkActionButtons={false}>
        <TextField label="ID" sortBy="id" source="originId" />
        <TooltipTextLinkField
          label={translate('app.table.name')}
          resource="companies"
          source="name"
          type="show"
        />
        <WrapperField>
          <Fragment />
        </WrapperField>
        <FunctionField
          label={translate('app.table.status')}
          render={({ archive }: Company) => (
            <ArchiveConditionListChip
              activeLabel={translate('app.companies.activeStatus')}
              archive={archive}
            />
          )}
          sortable={false}
          source="archive"
        />
        <FunctionField
          label={translate('app.table.merchantsAmount')}
          render={({ merchantCount, id }: Company) => {
            return (
              <LinkButton
                label={String(merchantCount)}
                pathParams={{
                  resource: Resources.Merchants,
                  type: 'list',
                }}
                queryParams={{
                  filter: { companies: [id] },
                }}
                sx={{ textDecoration: 'underline' }}
              />
            );
          }}
          sortable={false}
          source="merchantCount"
        />
        <DateTimeField label={translate('app.table.createdAt')} source="createdAt" />
        <DateTimeField label={translate('app.table.updatedAt')} source="updatedAt" />
        <ContextMenu
          render={(record: Company) => [
            hasResourceAccess(Resources.UserActions) && (
              <ContextMenuEntityUserActionsButton entityName="company" key="user-action-button" />
            ),
            <ContextMenuShowButton key="show-button" />,
            hasResourcePermissions(Resources.Companies, [Permission.Full, Permission.Update]) &&
              (record.archive ? (
                <ContextMenuUnarchiveButton
                  key="unarchive-button"
                  onClick={() => handleOpenRecordModalUnarchive(record)}
                />
              ) : (
                <ContextMenuEditButton key="edit-button" />
              )),
            hasResourcePermissions(Resources.Companies, [Permission.Full, Permission.Delete]) &&
              (record.archive ? (
                <ContextMenuDeleteButton
                  key="delete-button"
                  onClick={() => handleOpenRecordModalDelete(record)}
                />
              ) : (
                <ContextMenuArchiveButton
                  key="archive-button"
                  onClick={() => handleOpenRecordModalArchive(record)}
                />
              )),
          ]}
        />
      </StyledDatagrid>
      {openRecordModalDelete && (
        <CompaniesDeleteDialog
          onClose={handleCloseRecordModalDelete}
          open={openRecordModalDelete}
          record={recordDelete}
        />
      )}
      {openRecordModalUnarchive && recordUnarchive && (
        <CompaniesUnarchiveDialog
          onClose={handleCloseRecordModalUnarchive}
          open={openRecordModalUnarchive}
          record={recordUnarchive}
        />
      )}
      {openRecordModalArchive && recordArchive && (
        <CompaniesArchiveDialog
          onClose={handleCloseRecordModalArchive}
          open={openRecordModalArchive}
          record={recordArchive}
        />
      )}
    </ListPage>
  );
};
